import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import STEP_ROUTES from '../../../constants/proposal-step-routes';
import {
    LIVE_CAMPAIGN_PROPOSAL_SUBMIT_SUCCESS_EVENT,
    LIVE_CAMPAIGN_PROPOSAL_SUBMIT_ERROR_EVENT,
    LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS
} from '../../../constants/analytics-events';

const PROPOSAL_EXPANDS =
    'expand[]=product_selection_items&expand[]=product_selection_items.product&expand[]=product_selection_items.variant&expand[]=product_selection_items.product.variants&expand[]=steps&expand[]=tasks&expand[]=tasks&expand[]=contact.networks.network&expand[]=contact.primary_address&expand[]=campaign.brand&expand[]=jobs&expand[]=files&expand[]=content_rights';

export default {
    data() {
        return {
            isPatchingProposal: false,
            isCompletingProposal: false
        };
    },
    computed: {
        liveId() {
            return get(this.contact, 'live_url');
        },
        proposalId() {
            return get(this.proposal, 'id');
        },
        proposalParamId() {
            return this.$route.params.proposal_id;
        },
        proposals() {
            return this.$root.v2.proposals;
        },
        proposal() {
            return this.$root.v2.proposal;
        },
        isProposalDirty() {
            return this.proposalId !== this.proposalParamId;
        },
        createProposalUrl() {
            return `/api/v1/contacts/${this.contactId}/campaigns/${this.campaignParamId}/proposals`;
        },
        proposalUrl() {
            return `/api/v1/contacts/${this.contactId}/campaigns/${this.campaignParamId}/proposals/${this.proposalParamId}?${PROPOSAL_EXPANDS}`;
        },
        proposalsUrl() {
            return `/api/v1/contacts/${this.contactId}/campaigns/${this.campaignParamId}/proposals?expand[]=steps`;
        },
        proposalCompleteUrl() {
            return `/api/v1/contacts/${this.contactId}/campaigns/${this.campaignId}/proposals/${this.proposalId}/complete?${PROPOSAL_EXPANDS}`;
        },
        incompleteProposal() {
            const incompleteProposals = this.proposals.filter((proposal) => {
                return !proposal.is_complete;
            });

            return sortBy(incompleteProposals, ['created_at'])[incompleteProposals.length - 1]; // Get most recently created incomplete proposal.
        },
        proposalStepsOrder() {
            return get(this.$root.v2, 'proposal.steps', []);
        },
        isProposalPreview() {
            return !!this.$route.query.preview;
        }
    },
    methods: {
        createProposal() {
            return this.$http.post(this.createProposalUrl);
        },
        loadProposal(force = false) {
            if (!this.isProposalDirty && this.proposal && !force) {
                return Promise.resolve();
            }

            return this.$http.get(this.proposalUrl).then((res) => {
                this.$root.v2.proposal = res.data;

                if (moment().subtract(1, 'days') > moment(this.proposal.created_at) && !this.proposal.is_complete) {
                    throw new Error('Expired Proposal');
                }

                if (this.$root.v2.proposal.campaign) {
                    this.$root.v2.campaign = {
                        ...this.$root.v2.campaign,
                        ...this.updateCampaignState(this.$root.v2.proposal.campaign)
                    };
                }

                if (this.$root.v2.proposal.contact) {
                    this.$root.v2.contact = {
                        ...this.$root.v2.contact,
                        ...this.$root.v2.proposal.contact
                    };
                }

                if (
                    this.$root.v2.proposal.product_selection_items &&
                    this.$root.v2.proposal.product_selection_items.length > 0
                ) {
                    // Map selected products into data format needed
                    this.$root.v2.loadedProducts = this.$root.v2.proposal.product_selection_items.map((selection) => {
                        if (selection.variant) {
                            selection.product.variant = selection.variant;
                        }
                        return selection.product;
                    });
                }
            });
        },
        loadProposals() {
            return this.$http.get(this.proposalsUrl).then((res) => {
                this.$root.v2.proposals = res.data;
                this.$root.proposals = res.data;
            });
        },
        getProposalStepUrl(name, complete = false) {
            const tail = complete ? '/complete' : '';
            return `/api/v1/proposals/${this.$route.params.proposal_id}/steps/${name}${tail}`;
        },
        postProposalStep(data) {
            return this.$http.post(this.getProposalStepUrl(data.step), data.payload).then((response) => {
                this.updateStep(response.data);
            });
        },
        initProposalStep(step) {
            if (step.is_started) {
                return Promise.resolve();
            }
            this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS[step.name].start);
            return this.postProposalStep({
                step: step.name,
                payload: {}
            });
        },
        goNextStep(currentStep, payload) {
            const maxIndex = this.proposalStepsOrder.length - 1;
            const index = this.proposalStepsOrder.findIndex((step) => step.name === currentStep.name);
            const nextIndex = currentStep.is_complete ? maxIndex + 1 : index + 1;

            const nextStep = this.proposalStepsOrder[nextIndex];

            this.resetErrors();
            this.isCompletingProposal = true;

            return this.$http
                .patch(this.getProposalStepUrl(currentStep.name, true), payload)
                .then((response) => {
                    this.updateStep(response.data);
                    this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS[currentStep.name].complete);
                    return this.loadProposal(true);
                })
                .then(() => {
                    if (nextIndex <= maxIndex) {
                        return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
                    }
                    return this.$router.push({ name: 'v2.campaign.proposal' });
                })
                .catch(this.handleHttpError)
                .then(() => {
                    this.isCompletingProposal = false;
                });
        },
        goPreviousStep(currentStep) {
            const index = this.proposalStepsOrder.findIndex((step) => step.name === currentStep.name);

            if (index <= 0) {
                return this.$router.push({ name: 'v2.campaign.proposal' });
            }

            const nextIndex = index - 1;
            const nextStep = this.proposalStepsOrder[nextIndex];
            return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
        },
        updateStep(updatedStep) {
            // Clean up what we don't need
            delete updatedStep.proposal;
            delete updatedStep.campaign;

            const index = get(this.$root.v2, 'proposal.steps', []).findIndex((step) => step.name === updatedStep.name);

            if (index > -1) {
                this.$root.v2.proposal.steps.splice(index, 1, updatedStep);
            }
        },
        completeProposal() {
            this.resetErrors();
            this.isCompletingProposal = true;

            return this.$http
                .patch(this.proposalCompleteUrl)
                .then((response) => {
                    this.$root.v2.proposal = {
                        ...this.$root.v2.proposal,
                        ...response.data
                    };
                    this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_SUBMIT_SUCCESS_EVENT);
                })
                .catch((error) => {
                    this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_SUBMIT_ERROR_EVENT);
                    this.handleHttpError(error, 'proposal');
                })
                .then(() => {
                    this.isCompletingProposal = false;
                });
        }
    }
};
